import React, { useMemo, useState } from "react";
import {
  DemoResponsiveGrid,
  DemoSlider,
  DemoWorkbenchCard,
  DemoWorkbenchCopy,
  DemoWorkbenchInset,
  DemoWorkbenchMetricCard,
  DemoWorkbenchPill,
  DemoWorkbenchStack,
} from "@csk/demo-ui";
import {
  axiomDerivations,
  evaluateDiagonalTorus,
  formatDiagonal,
  type AxiomDerivation,
  type DiagonalPair,
} from "./diagonal-torus-panel/diagonalTorusModel.js";

const AXIOM_COLOR = {
  success: "#22c55e",
  warning: "#f59e0b",
} as const;

const DiagonalTorusPanel = (): React.JSX.Element => {
  const [aReal, setAReal] = useState(2);
  const [dReal, setDReal] = useState(3);
  const state = useMemo(
    () => evaluateDiagonalTorus(aReal, dReal),
    [aReal, dReal],
  );
  const axioms = useMemo(() => axiomDerivations(state), [state]);

  return (
    <DemoWorkbenchStack gap={24}>
      <DemoWorkbenchCard title="Diagonal Torus & Axiom Derivation (Preface, §12.8)">
        <DemoWorkbenchCopy>
          The diagonal torus T² ⊆ GL(2,ℂ) is the simplest authority domain. One
          choice → four axioms → ten guarantees.
        </DemoWorkbenchCopy>
      </DemoWorkbenchCard>

      <DemoWorkbenchCard compact title="Torus element: diag(a, d)">
        <DemoResponsiveGrid minColumnWidth={220} gap={16}>
          <DemoSlider
            id="diagonal-torus-a"
            label="a (Authority)"
            min={0.1}
            max={5}
            step={0.1}
            value={aReal}
            onChange={setAReal}
            format={(value) => value.toFixed(1)}
          />
          <DemoSlider
            id="diagonal-torus-d"
            label="d (Data)"
            min={0.1}
            max={5}
            step={0.1}
            value={dReal}
            onChange={setDReal}
            format={(value) => value.toFixed(1)}
          />
        </DemoResponsiveGrid>

        <DemoResponsiveGrid minColumnWidth={150} gap={12} className="mt-4">
          <MatrixBlock
            title="Matrix"
            pair={state.element}
            note="zeros = SEPARATE"
          />
          <MatrixBlock
            title="Product (self × self)"
            pair={state.product}
            note="still diagonal"
          />
          <MatrixBlock
            title="Inverse"
            pair={state.inverse}
            precision={2}
            note="still diagonal"
          />
        </DemoResponsiveGrid>
      </DemoWorkbenchCard>

      <DemoWorkbenchCard compact title="Axiom Derivation: A1 → A2 → A3 → A4">
        <DemoWorkbenchStack gap={8}>
          {axioms.map((axiom) => (
            <AxiomRow key={axiom.code} axiom={axiom} />
          ))}
        </DemoWorkbenchStack>
        <div className="mt-3 text-center text-sm font-bold text-teal-400">
          3/4 axioms FREE from GL(2,ℂ). Only A1 is your decision.
        </div>
      </DemoWorkbenchCard>

      <DemoWorkbenchCard compact title="Governance Space Layering">
        <div className="rounded border-2 border-gray-600 p-3">
          <div className="mb-1 text-xs text-gray-500">
            GL(n,ℂ) — all invertible matrices
          </div>
          <div className="rounded border-2 border-blue-500/50 bg-blue-900/10 p-3">
            <div className="mb-1 text-xs text-blue-400">
              C — certified manifold (Regime 1, all 10 NTs)
            </div>
            <div className="rounded border-2 border-teal-500/50 bg-teal-900/10 p-3">
              <div className="mb-1 text-xs text-teal-400">
                T^n — diagonal torus (subgroup, SEPARATE by construction)
              </div>
              <div className="py-2 text-center">
                <span className="font-mono text-sm text-teal-300">
                  diag({state.element.a.toFixed(1)},{" "}
                  {state.element.d.toFixed(1)}) ∈ T²
                </span>
              </div>
            </div>
            <div className="mt-1 text-xs text-blue-300">
              T^n is a subgroup. C is a manifold (not a subgroup — composition
              can exit C).
            </div>
          </div>
          <div className="mt-1 text-xs text-gray-400">
            Outside C: Regime 2/3 (some NTs hold, not all). det=0 boundary:
            singular.
          </div>
        </div>
      </DemoWorkbenchCard>

      <DemoResponsiveGrid minColumnWidth={150} gap={8}>
        <DemoWorkbenchMetricCard
          label="det(M)"
          value={state.determinant.toFixed(2)}
          color={state.invertible ? "#22c55e" : "#ef4444"}
          sub={state.invertible ? "invertible" : "SINGULAR"}
        />
        <DemoWorkbenchMetricCard
          label="tr(M)"
          value={state.trace.toFixed(2)}
          color="#cbd5e1"
          sub="resource rate"
        />
        <DemoWorkbenchMetricCard
          label="off-diag ‖δ‖"
          value="0.00"
          color="#22c55e"
          sub="SEPARATE ✓"
        />
      </DemoResponsiveGrid>
    </DemoWorkbenchStack>
  );
};

function MatrixBlock({
  title,
  pair,
  note,
  precision = 1,
}: {
  readonly title: string;
  readonly pair: DiagonalPair | null;
  readonly note: string;
  readonly precision?: number;
}): React.JSX.Element {
  const [left, right] = formatDiagonal(pair, precision);
  return (
    <DemoWorkbenchInset compact title={title}>
      <div className="rounded border border-gray-700 bg-gray-900 p-2 text-center font-mono text-gray-200">
        [{left}, <span className="text-green-500">0</span>]
        <br />[<span className="text-green-500">0</span>, {right}]
      </div>
      <div className="mt-1 text-center text-xs text-green-400">{note}</div>
    </DemoWorkbenchInset>
  );
}

function AxiomRow({
  axiom,
}: {
  readonly axiom: AxiomDerivation;
}): React.JSX.Element {
  const color = AXIOM_COLOR[axiom.tone];
  return (
    <DemoWorkbenchStack gap={4}>
      {axiom.derivation !== undefined ? (
        <div className="flex items-center gap-1 pl-4">
          <span className="text-gray-600">↓</span>
          <span className="text-xs italic text-gray-500">
            {axiom.derivation}
          </span>
        </div>
      ) : null}
      <DemoWorkbenchInset
        compact
        title={axiom.title}
        right={
          <span
            className="flex h-8 w-8 items-center justify-center rounded-full border text-xs font-bold"
            style={{
              background: `${color}26`,
              borderColor: `${color}80`,
              color,
            }}
          >
            {axiom.code}
          </span>
        }
      >
        <div className="text-sm text-slate-400">{axiom.explanation}</div>
        <div className="mt-2">
          <DemoWorkbenchPill variant={axiom.tone}>
            {axiom.status}
          </DemoWorkbenchPill>
        </div>
      </DemoWorkbenchInset>
    </DemoWorkbenchStack>
  );
}

export default DiagonalTorusPanel;
